"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function (o) {
    var m = typeof Symbol === "function" && o[Symbol.iterator], i = 0;
    if (m) return m.call(o);
    return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
};
Object.defineProperty(exports, "__esModule", { value: true });
var React = require("react");
var ReactDOM = require("react-dom");
var Animator_1 = require("./Animator");
var InternalDataSource_1 = require("./data-source/InternalDataSource");
var ManifestDataSource_1 = require("./data-source/ManifestDataSource");
var AppCanvas_1 = require("./ui/core/AppCanvas");
var TrackViewer_1 = require("./ui/TrackViewer");
var interval_1 = require("./track/interval");
var AnnotationTileLoader_1 = require("./track/annotation/AnnotationTileLoader");
var AnnotationTrack_1 = require("./track/annotation/AnnotationTrack");
var SequenceTileLoader_1 = require("./track/sequence/SequenceTileLoader");
var SequenceTrack_1 = require("./track/sequence/SequenceTrack");
var VariantTileLoader_1 = require("./track/variant/VariantTileLoader");
var VariantTrack_1 = require("./track/variant/VariantTrack");
var SignalTileLoader_1 = require("./track/signal/SignalTileLoader");
var SignalTrack_1 = require("./track/signal/SignalTrack");
var Formats_1 = require("./formats/Formats");
var GenomeVisualizer = /** @class */ (function () {
    function GenomeVisualizer(configuration, dataSource) {
        var e_1, _a;
        var _this = this;
        this._frameLoopHandle = 0;
        this.frameLoop = function () {
            if (_this.appCanvasRef == null)
                return;
            _this._frameLoopHandle = window.requestAnimationFrame(_this.frameLoop);
            // appCanvas should react to user input before animation are stepped
            // this enables any animations spawned by the interaction events to be progressed before rendering
            _this.appCanvasRef.handleUserInteraction();
            Animator_1.Animator.frame();
            _this.appCanvasRef.renderCanvas();
        };
        this.trackViewer = new TrackViewer_1.TrackViewer();
        this.setDataSource(dataSource);
        if (Array.isArray(configuration)) {
            if (configuration.length > 0) {
                try {
                    // add tracks from path list
                    for (var configuration_1 = __values(configuration), configuration_1_1 = configuration_1.next(); !configuration_1_1.done; configuration_1_1 = configuration_1.next()) {
                        var path = configuration_1_1.value;
                        this.addTrackFromFilePath(path, undefined, false);
                    }
                }
                catch (e_1_1) { e_1 = { error: e_1_1 }; }
                finally {
                    try {
                        if (configuration_1_1 && !configuration_1_1.done && (_a = configuration_1.return)) _a.call(configuration_1);
                    }
                    finally { if (e_1) throw e_1.error; }
                }
            }
        }
        else {
            if (configuration != null) {
                this.setConfiguration(configuration);
            }
        }
    }
    GenomeVisualizer.prototype.setDataSource = function (dataSourceArg) {
        var dataSource;
        if ((typeof dataSourceArg === 'string') || (dataSourceArg == null)) {
            // if first argument is string, use a manifest data source
            // if a manifest data source is created with a null path then it acts as an empty manifest
            dataSource = new ManifestDataSource_1.ManifestDataSource(dataSourceArg);
        }
        else {
            dataSource = dataSourceArg;
        }
        if (this.internalDataSource != null) {
            this.internalDataSource.clearTileCaches();
            this.internalDataSource = null;
        }
        this.internalDataSource = new InternalDataSource_1.InternalDataSource(dataSource);
        this.trackViewer.setDataSource(this.internalDataSource);
    };
    GenomeVisualizer.prototype.setConfiguration = function (configuration) {
        this.trackViewer.setConfiguration(configuration);
    };
    GenomeVisualizer.prototype.getConfiguration = function () {
        return this.trackViewer.getConfiguration();
    };
    /**
     * Sets the current displayed genomic location (contig, region) of the first open panel
     * @param genomicLocation `{contig: string, x0: number, x1: number}`
     */
    GenomeVisualizer.prototype.setLocation = function (genomicLocation) {
        this.setContig(genomicLocation.contig);
        this.setRange(genomicLocation.x0, genomicLocation.x1);
    };
    /**
     * Sets the current displayed contig of the first open panel
     * Use with `setRange()` to specify a complete location
     * @param contig id of contig within available data
     */
    GenomeVisualizer.prototype.setContig = function (contig) {
        if (this.getPanels().length > 0) {
            this.getPanels()[0].setContig(contig);
        }
    };
    /**
     * Sets the current displayed region of the first open panel
     * Spanned length = x1 - x0
     * @param x0 left base index (starting at 0)
     * @param x1 right base index
     */
    GenomeVisualizer.prototype.setRange = function (x0, x1) {
        if (this.getPanels().length > 0) {
            this.getPanels()[0].setRange(x0, x1);
        }
    };
    GenomeVisualizer.prototype.addTrack = function (model, animateIn) {
        if (animateIn === void 0) { animateIn = true; }
        return this.trackViewer.addTrack(model, animateIn);
    };
    GenomeVisualizer.prototype.addTrackFromFilePath = function (path, name, animateIn) {
        // we don't know what contigs are available so we must read the first file for this
        var basename = path.split('/').pop().split('\\').pop();
        var parts = basename.split('.');
        parts.pop();
        var filename = parts.join('.');
        var trackName = (name != null ? name : filename);
        var format = Formats_1.Formats.determineFormat(path);
        switch (format) {
            case Formats_1.GenomicFileFormat.BigWig: {
                var model = {
                    type: 'signal',
                    name: trackName,
                    path: path,
                };
                return this.addTrack(model, animateIn);
            }
            case Formats_1.GenomicFileFormat.ValisGenes:
            case Formats_1.GenomicFileFormat.BigBed:
                {
                    var model = {
                        type: 'annotation',
                        name: trackName,
                        path: path,
                    };
                    return this.addTrack(model, animateIn);
                }
            case Formats_1.GenomicFileFormat.ValisDna: {
                var model = {
                    type: 'sequence',
                    name: trackName,
                    path: path,
                };
                return this.addTrack(model, animateIn);
            }
            case Formats_1.GenomicFileFormat.ValisVariants: {
                var model = {
                    type: 'variant',
                    name: trackName,
                    path: path,
                };
                return this.addTrack(model, animateIn);
            }
            /*
            case 'bam': { break; }
            case 'vcf': { break; }
            case 'fasta': { break; }
            case 'gff3': { break; }
            */
            default: {
                console.error("Error adding track: Unsupported file \"" + path + "\"");
                break;
            }
        }
        return null;
    };
    GenomeVisualizer.prototype.addPanel = function (location, animateIn) {
        return this.trackViewer.addPanel(location, animateIn);
    };
    GenomeVisualizer.prototype.closeTrack = function (track, animateOut, onComplete) {
        if (animateOut === void 0) { animateOut = true; }
        return this.trackViewer.closeTrack(track, animateOut, onComplete);
    };
    GenomeVisualizer.prototype.closePanel = function (panel, animateOut, onComplete) {
        return this.trackViewer.closePanel(panel, animateOut, onComplete);
    };
    GenomeVisualizer.prototype.setTrackIndex = function (track, index, animate) {
        if (animate === void 0) { animate = true; }
        return this.trackViewer.setTrackIndex(track, index, animate);
    };
    GenomeVisualizer.prototype.moveTrackUp = function (track, animate) {
        if (animate === void 0) { animate = true; }
        return this.trackViewer.moveTrackUp(track, animate);
    };
    GenomeVisualizer.prototype.moveTrackDown = function (track, animate) {
        if (animate === void 0) { animate = true; }
        return this.trackViewer.moveTrackDown(track, animate);
    };
    GenomeVisualizer.prototype.getTracks = function () {
        return this.trackViewer.getTracks();
    };
    GenomeVisualizer.prototype.getPanels = function () {
        return Array.from(this.trackViewer.getPanels());
    };
    GenomeVisualizer.prototype.clearCaches = function () {
        if (this.internalDataSource != null) {
            this.internalDataSource.clearTileCaches();
        }
    };
    GenomeVisualizer.prototype.addEventListener = function (event, listener) {
        this.trackViewer.addEventListener(event, listener);
    };
    GenomeVisualizer.prototype.removeEventListener = function (event, listener) {
        this.trackViewer.removeEventListener(event, listener);
    };
    GenomeVisualizer.prototype.getContentHeight = function () {
        return this.trackViewer.getContentHeight();
    };
    GenomeVisualizer.prototype.render = function (props, container) {
        ReactDOM.render(this.reactRender(props), container);
    };
    GenomeVisualizer.prototype.reactRender = function (props) {
        var _this = this;
        if (props === void 0) { props = {
            width: null,
            height: null,
        }; }
        var width = props.width == null ? 800 : props.width;
        var height = props.height == null ? 600 : props.height;
        return (React.createElement(React.Fragment, null,
            React.createElement(AppCanvas_1.AppCanvas, { ref: function (v) {
                    _this.appCanvasRef = v;
                    _this.startFrameLoop();
                }, width: width, height: height, className: 'hpgv', content: this.trackViewer, pixelRatio: props.pixelRatio || window.devicePixelRatio || 1, style: __assign({ 
                    // default style
                    fontFamily: 'sans-serif' }, props.style), onWillUnmount: function () {
                    _this.stopFrameLoop();
                    _this.clearCaches();
                } },
                React.createElement("div", { className: "hpgv_style-proxies", style: { display: 'none' } }, this.trackViewer.getStyleNodes()))));
    };
    /**
     * This method will update non-dom elements relying on CSS.
     * Useful to call after the CSS changes, however, if the inline style on style proxy node changes then the update will happen automatically.
     */
    GenomeVisualizer.prototype.refreshStyle = function () {
        this.trackViewer.refreshStyle();
    };
    GenomeVisualizer.prototype.startFrameLoop = function () {
        if (this._frameLoopHandle === 0) {
            this.frameLoop();
        }
    };
    GenomeVisualizer.prototype.stopFrameLoop = function () {
        if (this._frameLoopHandle !== 0) {
            window.cancelAnimationFrame(this._frameLoopHandle);
            this._frameLoopHandle = 0;
        }
    };
    GenomeVisualizer.registerTrackType = function (type, tileLoaderClass, trackObjectClass) {
        this.trackTypes[type] = {
            tileLoaderClass: tileLoaderClass,
            trackObjectClass: trackObjectClass,
        };
    };
    GenomeVisualizer.getTrackType = function (type) {
        var trackClass = this.trackTypes[type];
        if (trackClass == null) {
            console.warn("No track type \"" + type + "\", available types are: " + Object.keys(this.trackTypes).join(', '));
        }
        return trackClass;
    };
    GenomeVisualizer.getTrackTypes = function () {
        return Object.keys(this.trackTypes);
    };
    GenomeVisualizer.setTheme = function (theme) {
        var defaultTheme = 'light';
        this.setBaseStyle(require('./styles/' + (theme || defaultTheme) + '.css'));
    };
    GenomeVisualizer.setBaseStyle = function (cssString) {
        var hpgvStyleEl = document.head.querySelector('style#hpgv-base');
        // if null, remove any existing base css
        if (cssString == null) {
            if (hpgvStyleEl != null) {
                hpgvStyleEl.remove();
            }
            return;
        }
        if (hpgvStyleEl == null) {
            // add hpgv style
            hpgvStyleEl = document.createElement('style');
            hpgvStyleEl.id = 'hpgv-base';
            document.head.prepend(hpgvStyleEl);
        }
        hpgvStyleEl.innerHTML = cssString;
    };
    GenomeVisualizer.removeBaseStyle = function () {
        var hpgvStyleEl = document.head.querySelector('style#hpgv-base');
        if (hpgvStyleEl != null) {
            hpgvStyleEl.remove();
        }
    };
    GenomeVisualizer.trackTypes = {};
    return GenomeVisualizer;
}());
exports.GenomeVisualizer = GenomeVisualizer;
// register track types
GenomeVisualizer.registerTrackType('annotation', AnnotationTileLoader_1.AnnotationTileLoader, AnnotationTrack_1.AnnotationTrack);
GenomeVisualizer.registerTrackType('interval', interval_1.IntervalTileLoader, interval_1.IntervalTrack);
GenomeVisualizer.registerTrackType('sequence', SequenceTileLoader_1.SequenceTileLoader, SequenceTrack_1.SequenceTrack);
GenomeVisualizer.registerTrackType('variant', VariantTileLoader_1.VariantTileLoader, VariantTrack_1.VariantTrack);
GenomeVisualizer.registerTrackType('signal', SignalTileLoader_1.SignalTileLoader, SignalTrack_1.SignalTrack);
GenomeVisualizer.setTheme('light');
exports.default = GenomeVisualizer;
